import { StandardEvents } from '@shopify/events';

const readState = (key) => {
  try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch { return null; }
};
const writeState = (key, value) => {
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* Shopping works without storage. */ }
};

class CubeBuffOptionSheet extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.dialog = this.querySelector('dialog');
    this.section = this.closest('.shopify-section');
    this.busy = false;
    this.ready = !this.querySelector('[data-cb-add]').disabled;
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cb-configure]')) {
        this.opener = event.target.closest('button');
        this.openScroll = window.scrollY;
        this.dialog.showModal();
        document.documentElement.classList.add('cb-options-open');
      }
      if (event.target.closest('[data-cb-close]')) this.dialog.close();
      const option = event.target.closest('[data-cb-variant]');
      if (option && !option.disabled && !this.busy) this.select(option.dataset.cbVariant);
      if (event.target.closest('[data-cb-add]')) this.add();
    }, { signal });
    this.dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('cb-options-open');
      this.opener?.focus({ preventScroll: true });
      requestAnimationFrame(() => window.scrollTo(0, this.openScroll || 0));
    }, { signal });
    this.section.addEventListener(StandardEvents.productSelect, (event) => {
      if (event.target.closest('product-card')) return;
      this.ready = false;
      this.setBusy(true, 'Updating your selection…');
      event.promise?.then(({ detail }) => {
        const variant = detail?.resource;
        if (String(detail?.productId) !== this.dataset.productId) return;
        this.dataset.selectedVariant = variant?.id || '';
        const option = this.querySelector(`[data-cb-variant="${variant?.id}"]`);
        if (option) {
          this.querySelectorAll('[data-cb-variant]').forEach((button) => button.setAttribute('aria-pressed', String(button === option)));
          this.querySelectorAll('[data-cb-selected-title]').forEach((node) => { node.textContent = option.dataset.title; });
          this.querySelectorAll('[data-cb-selected-price]').forEach((node) => { node.textContent = option.dataset.price; });
        }
        this.ready = Boolean(variant?.available);
        this.setBusy(false, this.ready ? `${option?.dataset.title || 'Option'} selected. Display product only; LEGO® model not included.` : 'This option is currently unavailable.');
      }).catch((error) => {
        if (error?.name === 'AbortError') return;
        this.ready = false;
        this.setBusy(false, 'Your selection could not be updated. Please choose an option again.');
      });
    }, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      if (!this.adding) return;
      event.promise?.then(() => {
        this.adding = false;
        this.setBusy(false, 'Added to your bag. You can continue browsing or review your order.');
        this.querySelector('[data-cb-view-bag]').hidden = false;
      }).catch(() => {
        this.adding = false;
        this.setBusy(false, 'This item could not be added. Please check the product message and try again.');
      });
    }, { signal });
    document.addEventListener(StandardEvents.cartError, () => {
      if (!this.adding) return;
      this.adding = false;
      this.setBusy(false, 'This item could not be added. Please check the product message and try again.');
    }, { signal });
    this.classList.add('is-ready');
  }
  disconnectedCallback() {
    this.controller.abort();
    document.documentElement.classList.remove('cb-options-open');
  }
  select(id) {
    const input = this.section.querySelector(`variant-picker input[data-variant-id="${id}"]`);
    if (input && !input.disabled) input.click();
  }
  setBusy(busy, message) {
    this.busy = busy;
    this.querySelector('[data-cb-status]').textContent = message;
    this.querySelectorAll('[data-cb-add]').forEach((button) => {
      button.disabled = busy || !this.ready;
      button.textContent = busy ? (this.adding ? 'Adding…' : 'Updating…') : this.ready ? 'Add to bag' : 'Unavailable';
    });
    this.querySelector('.cb-option-choices').setAttribute('aria-busy', String(busy));
  }
  add() {
    if (this.busy || !this.ready) return;
    const button = this.section.querySelector('product-form-component [ref="addToCartButton"]');
    if (!button || button.disabled) return;
    this.adding = true;
    this.setBusy(true, 'Adding your selected display…');
    button.click();
  }
}
if (!customElements.get('cubebuff-option-sheet')) customElements.define('cubebuff-option-sheet', CubeBuffOptionSheet);

// Shopify keeps filters and variants in the URL; store only presentation state.
const pageKey = () => `cb-view:${location.pathname}${location.search}`;
const productPath = location.pathname.startsWith('/products/');
if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward' && readState(pageKey())) {
  history.scrollRestoration = 'manual';
}
window.addEventListener('pagehide', () => {
  writeState(pageKey(), { y: window.scrollY });
  if (productPath) {
    const gallery = document.querySelector('.cb-product-page media-gallery');
    const picker = document.querySelector('.cb-product-page variant-picker');
    writeState(`cb-product:${location.pathname}`, {
      variant: picker?.querySelector('input:checked')?.dataset.variantId,
      slide: Math.max(0, [...(gallery?.querySelectorAll('slideshow-slide') || [])].findIndex((slide) => slide.getAttribute('aria-hidden') === 'false')),
    });
  }
});
async function restoreProductView() {
  const initialProductState = productPath ? readState(`cb-product:${location.pathname}`) : null;
  if (!initialProductState) return;
  await customElements.whenDefined('variant-picker');
  const picker = document.querySelector('.cb-product-page variant-picker');
  if (!picker) return;
  const restoreGallery = async () => {
    const slideshow = document.querySelector('.cb-product-page media-gallery slideshow-component');
    slideshow?.setAttribute('initial-slide', String(initialProductState.slide || 0));
    await customElements.whenDefined('slideshow-component');
    requestAnimationFrame(() => {
      slideshow?.select(initialProductState.slide || 0, undefined, { animate: false });
    });
  };
  const variant = new URL(location.href).searchParams.get('variant');
  const input = picker.querySelector(`input[data-variant-id="${initialProductState.variant}"]`);
  if (!variant && input && !input.checked && !input.disabled) {
    picker.addEventListener(StandardEvents.productSelect, (event) => {
      event.promise?.then(restoreGallery).catch(() => {});
    }, { once: true });
    input.click();
  } else if (!variant || variant === String(initialProductState.variant)) {
    restoreGallery();
  }
}
window.addEventListener('pageshow', (event) => {
  restoreProductView();
  if (event.persisted || performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
    const state = readState(pageKey());
    if (state) requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, state.y || 0)));
  }
});
